/**
 * ✅ FERP CONVERSION PROGRESS TRACKER
 *
 * Sprint 8 LLM A Track - Day 3-4
 *
 * Real-Time Conversion Progress Visualization
 * - 11-phase progress timeline with live updates
 * - Multi-LLM result comparison (Claude, GPT-4, Gemini)
 * - Confidence scoring visualization
 * - Validation error display
 * - Estimated time remaining
 * - WebSocket-powered real-time updates
 *
 * ARCHITECTURE:
 * - Mantine UI only
 * - Data-driven (all data from useConversionProgress hook)
 * - Multi-tenant isolation
 * - Immutable progress history
 *
 * USAGE:
 * ```tsx
 * <ConversionProgressTracker
 *   documentId="doc-123"
 *   onComplete={(confidence) => console.log('Done:', confidence)}
 * />
 * ```
 */

import React from 'react';
import {
  Card,
  Stack,
  Group,
  Text,
  Badge,
  Progress,
  Timeline,
  Alert,
  Table,
  ThemeIcon,
  RingProgress,
  Grid,
  Title,
  Tooltip,
  Code,
  Accordion,
} from '@mantine/core';
import {
  IconCheck,
  IconX,
  IconClock,
  IconAlertCircle,
  IconBrain,
  IconChartBar,
  IconFileCheck,
  IconLoader,
  IconRobot,
  IconCircleCheck,
  IconAlertTriangle,
} from '@tabler/icons-react';
import {
  useConversionProgress,
  type ConversionPhase,
  type LLMResult,
  type ValidationError,
} from './hooks/useConversionProgress';

// ============================================================
// Component Props
// ============================================================

export interface ConversionProgressTrackerProps {
  /**
   * Document ID to track conversion for
   */
  documentId: string;

  /**
   * Callback when conversion completes successfully
   */
  onComplete?: (confidence: number) => void;

  /**
   * Callback when conversion fails
   */
  onError?: (error: Error) => void;

  /**
   * Show detailed LLM comparison (default: true)
   */
  showLLMComparison?: boolean;

  /**
   * Show validation errors (default: true)
   */
  showValidationErrors?: boolean;

  /**
   * Compact view (hides details)
   */
  compact?: boolean;
}

// ============================================================
// Main Component
// ============================================================

export const ConversionProgressTracker: React.FC<ConversionProgressTrackerProps> = ({
  documentId,
  onComplete,
  onError,
  showLLMComparison = true,
  showValidationErrors = true,
  compact = false,
}) => {
  const {
    currentProgress,
    phase,
    phaseName,
    status,
    message,
    overallProgress,
    phaseProgress,
    confidence,
    aggregatedConfidence,
    llmConsensus,
    estimatedTimeRemaining,
    timeElapsed,
    llmResults,
    validationErrors,
    criticalErrors,
    progressHistory,
    isComplete,
    isFailed,
    isLoading,
    error,
    getPhaseDescription,
    isPhaseCompleted,
  } = useConversionProgress(documentId);

  /**
   * Notify parent on completion
   */
  React.useEffect(() => {
    if (isComplete && onComplete) {
      onComplete(confidence);
    }
  }, [isComplete, confidence, onComplete]);

  /**
   * Notify parent on error
   */
  React.useEffect(() => {
    if (isFailed && error && onError) {
      onError(error);
    }
  }, [isFailed, error, onError]);

  if (!documentId) {
    return (
      <Alert color="blue" icon={<IconAlertCircle size={16} />}>
        No document selected for conversion
      </Alert>
    );
  }

  if (isLoading && !currentProgress) {
    return (
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Stack ta="center" gap="md">
          <IconLoader size={48} />
          <Text>Initializing conversion...</Text>
        </Stack>
      </Card>
    );
  }

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="md">
        {/* Header */}
        <Group justify="apart">
          <Group gap="xs">
            <ThemeIcon
              size="lg"
              variant="light"
              color={isComplete ? 'green' : isFailed ? 'red' : 'blue'}
            >
              {isComplete ? (
                <IconCircleCheck size={20} />
              ) : isFailed ? (
                <IconX size={20} />
              ) : (
                <IconLoader size={20} />
              )}
            </ThemeIcon>
            <div>
              <Title order={4}>Chaos-to-DSL Conversion</Title>
              <Text size="sm" c="dimmed">
                {isComplete
                  ? 'Conversion complete'
                  : isFailed
                    ? 'Conversion failed'
                    : `Phase ${phase}/11: ${phaseName}`}
              </Text>
            </div>
          </Group>

          {/* Time metrics */}
          <Group gap="md">
            <div>
              <Text size="xs" c="dimmed">
                Elapsed
              </Text>
              <Text size="sm" fw={500}>
                {formatDuration(timeElapsed)}
              </Text>
            </div>
            {!isComplete && !isFailed && estimatedTimeRemaining > 0 && (
              <div>
                <Text size="xs" c="dimmed">
                  Remaining
                </Text>
                <Text size="sm" fw={500}>
                  {formatDuration(estimatedTimeRemaining * 1000)}
                </Text>
              </div>
            )}
          </Group>
        </Group>

        {/* Overall Progress */}
        <Stack gap="xs">
          <Group justify="apart">
            <Text size="sm" fw={500}>
              Overall Progress
            </Text>
            <Text size="sm" fw={500}>
              {overallProgress}%
            </Text>
          </Group>
          <Progress
            value={overallProgress}
            size="lg"
            color={isComplete ? 'green' : isFailed ? 'red' : 'blue'}
            animated={!isComplete && !isFailed}
          />
        </Stack>

        {/* Current Status */}
        {message && (
          <Alert
            color={status === 'Failed' ? 'red' : status === 'Warning' ? 'orange' : 'blue'}
            icon={status === 'Failed' ? <IconX size={16} /> : <IconBrain size={16} />}
          >
            {message}
          </Alert>
        )}

        {/* Confidence Metrics */}
        {!compact && (confidence > 0 || llmResults.length > 0) && (
          <Grid>
            <Grid.Col span={4}>
              <ConfidenceRing
                value={confidence * 100}
                label="Overall Confidence"
                color={getConfidenceColor(confidence)}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <ConfidenceRing
                value={aggregatedConfidence * 100}
                label="Aggregated Confidence"
                color={getConfidenceColor(aggregatedConfidence)}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <ConfidenceRing
                value={llmConsensus}
                label="LLM Consensus"
                color={llmConsensus >= 80 ? 'green' : llmConsensus >= 60 ? 'yellow' : 'orange'}
              />
            </Grid.Col>
          </Grid>
        )}

        {/* Phase Timeline */}
        {!compact && (
          <Card withBorder padding="sm">
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Conversion Phases
              </Text>
              <Timeline active={phase - 1} lineWidth={2}>
                {([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11] as ConversionPhase[]).map((phaseNum) => (
                  <Timeline.Item
                    key={phaseNum}
                    bullet={
                      isPhaseCompleted(phaseNum) ? (
                        <IconCheck size={12} />
                      ) : phase === phaseNum ? (
                        <IconLoader size={12} />
                      ) : (
                        <IconClock size={12} />
                      )
                    }
                    title={getPhaseDescription(phaseNum)}
                    color={
                      isPhaseCompleted(phaseNum)
                        ? 'green'
                        : phase === phaseNum
                          ? 'blue'
                          : 'gray'
                    }
                  >
                    {phase === phaseNum && (
                      <Progress value={phaseProgress} size="sm" mt="xs" />
                    )}
                    {progressHistory.find((h) => h.phase === phaseNum) && (
                      <Text size="xs" c="dimmed" mt="xs">
                        {formatDuration(
                          progressHistory.find((h) => h.phase === phaseNum)?.durationMs || 0
                        )}
                      </Text>
                    )}
                  </Timeline.Item>
                ))}
              </Timeline>
            </Stack>
          </Card>
        )}

        {/* LLM Comparison */}
        {showLLMComparison && llmResults.length > 0 && (
          <Card withBorder padding="sm">
            <Stack gap="xs">
              <Group justify="apart">
                <Group gap="xs">
                  <IconRobot size={16} />
                  <Text size="sm" fw={500}>
                    Multi-LLM Analysis
                  </Text>
                </Group>
                <Badge color={llmConsensus >= 80 ? 'green' : 'orange'}>
                  {llmConsensus}% Consensus
                </Badge>
              </Group>

              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Provider</Table.Th>
                    <Table.Th>Model</Table.Th>
                    <Table.Th>Confidence</Table.Th>
                    <Table.Th>Processing Time</Table.Th>
                    <Table.Th>Tokens</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {llmResults.map((result) => (
                    <LLMResultRow key={result.provider} result={result} />
                  ))}
                </Table.Tbody>
              </Table>
            </Stack>
          </Card>
        )}

        {/* Validation Errors */}
        {showValidationErrors && validationErrors.length > 0 && (
          <Card withBorder padding="sm">
            <Stack gap="xs">
              <Group gap="xs">
                <IconAlertTriangle size={16} color={criticalErrors.length > 0 ? 'red' : 'orange'} />
                <Text size="sm" fw={500}>
                  Validation Issues ({validationErrors.length})
                </Text>
              </Group>

              <Accordion variant="separated">
                {validationErrors.map((error, idx) => (
                  <ValidationErrorItem key={idx} error={error} />
                ))}
              </Accordion>
            </Stack>
          </Card>
        )}

        {/* Error State */}
        {isFailed && error && (
          <Alert color="red" icon={<IconX size={16} />} title="Conversion Failed">
            {error.message}
          </Alert>
        )}

        {/* Success State */}
        {isComplete && (
          <Alert color="green" icon={<IconFileCheck size={16} />} title="Conversion Complete">
            Template generated successfully with {Math.round(confidence * 100)}% confidence.
            {confidence < 0.85 && ' Human review recommended for low-confidence fields.'}
          </Alert>
        )}
      </Stack>
    </Card>
  );
};

// ============================================================
// Sub-Components
// ============================================================

interface ConfidenceRingProps {
  value: number;
  label: string;
  color: string;
}

const ConfidenceRing: React.FC<ConfidenceRingProps> = ({ value, label, color }) => {
  return (
    <Card withBorder padding="sm">
      <Stack ta="center" gap="xs">
        <RingProgress
          sections={[{ value, color }]}
          size={80}
          thickness={8}
          label={
            <Text size="lg" fw={700} ta="center">
              {Math.round(value)}%
            </Text>
          }
        />
        <Text size="xs" ta="center" c="dimmed">
          {label}
        </Text>
      </Stack>
    </Card>
  );
};

interface LLMResultRowProps {
  result: LLMResult;
}

const LLMResultRow: React.FC<LLMResultRowProps> = ({ result }) => {
  const confidenceColor = getConfidenceColor(result.confidence);

  return (
    <Table.Tr>
      <Table.Td>
        <Badge color={getProviderColor(result.provider)}>{result.provider}</Badge>
      </Table.Td>
      <Table.Td>
        <Code>{result.modelVersion}</Code>
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          <Progress
            value={result.confidence * 100}
            size="sm"
            color={confidenceColor}
            style={{ width: '60px' }}
          />
          <Text size="sm" fw={500}>
            {Math.round(result.confidence * 100)}%
          </Text>
        </Group>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{result.processingTimeMs}ms</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{result.tokenCount.toLocaleString()}</Text>
      </Table.Td>
    </Table.Tr>
  );
};

interface ValidationErrorItemProps {
  error: ValidationError;
}

const ValidationErrorItem: React.FC<ValidationErrorItemProps> = ({ error }) => {
  const severityColor = error.severity === 'ERROR' ? 'red' : error.severity === 'WARNING' ? 'orange' : 'blue';

  return (
    <Accordion.Item value={`${error.field}-${error.message}`}>
      <Accordion.Control
        icon={
          <ThemeIcon color={severityColor} variant="light" size="sm">
            {error.severity === 'ERROR' ? <IconX size={14} /> : <IconAlertCircle size={14} />}
          </ThemeIcon>
        }
      >
        <Group justify="apart">
          <Text size="sm" fw={500}>
            {error.field}
          </Text>
          <Badge color={severityColor} size="sm">
            {error.severity}
          </Badge>
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        <Stack gap="xs">
          <div>
            <Text size="xs" c="dimmed">
              Issue:
            </Text>
            <Text size="sm">{error.message}</Text>
          </div>
          {error.suggestion && (
            <div>
              <Text size="xs" c="dimmed">
                Suggestion:
              </Text>
              <Text size="sm" c="blue">
                {error.suggestion}
              </Text>
            </div>
          )}
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
};

// ============================================================
// Helper Functions
// ============================================================

function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
}

function getConfidenceColor(confidence: number): string {
  if (confidence >= 0.9) return 'green';
  if (confidence >= 0.8) return 'lime';
  if (confidence >= 0.7) return 'yellow';
  if (confidence >= 0.6) return 'orange';
  return 'red';
}

function getProviderColor(provider: string): string {
  switch (provider) {
    case 'Claude':
      return 'violet';
    case 'GPT4':
      return 'green';
    case 'Gemini':
      return 'blue';
    case 'Llama':
      return 'orange';
    default:
      return 'gray';
  }
}
